// CampanaBar.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { tiendaDelVisitante, DOWNLOAD_URL } from "../utils/dispositivo";
import { campanaVigente, cuentaAtras, PRECIO_ANTES, PRECIO_AHORA } from "../utils/campana";
import { trackAppStoreClick, trackGooglePlayClick, trackCTAClick } from "../analytics/googleAnalytics";
import "./CampanaBar.css";

/**
 * La barra superior del sitio.
 *
 * Vivía dentro de `pages/Landing.jsx`, así que solo la veía quien entraba por la
 * portada: en /contador-calorias, /premium, las 15 landings de SEO y los 107
 * posts del blog -por donde entra la mayoría desde Google- no se anunciaba nada.
 * Ahora se monta una sola vez en `App.jsx`, encima de todas las rutas públicas.
 *
 * Tres cosas que hay que respetar al tocar esto:
 *
 * 1. ⚠️ NO DICE NINGÚN PRECIO. El porqué está en `utils/campana.js`.
 *
 * 2. La tienda del visitante se resuelve DESPUÉS del montaje, nunca al pintar.
 *    Este HTML lo genera también el prerender en Node, donde no existe
 *    `navigator`, y ese mismo HTML lo recibe todo el mundo. Lo estático sale
 *    neutro y cada navegador lo afina al arrancar.
 *
 * 3. La altura se publica en `--campana-alto`. El header es `position: sticky`
 *    con `top: 0` igual que esta barra, así que sin eso los dos se pegan al
 *    mismo sitio: el header quedaba ENCIMA tapando la barra a medias -es más
 *    bajo que ella- y por debajo asomaba el botón. Con la variable el header se
 *    pega justo debajo y quedan apilados. Se mide con ResizeObserver porque la
 *    barra cambia de alto al girar el móvil o al partirse el texto en dos.
 */

// Paneles privados y páginas que solo son un salto: ahí la barra estorba.
//
// /premium estuvo fuera hasta el 14-sep-2026: no se sabía si el Stripe de la web
// cobraba ya 29,99 o seguía en 49,99, y anunciar la oferta encima de un botón que
// cobrase el precio viejo no era una oferta. Comprobado en producción
// (scripts/informe-embudo.sh): STRIPE_ANNUAL_PRICE_ID es el de 29,99. Vuelve.
function rutaExcluida(pathname) {
  return (
    pathname.startsWith("/admin") ||
    pathname === "/coach" ||
    // La landing y el blog de ENTRENADORES: la barra anuncia la app de clientes
    // («Descargar la app»), y un entrenador que se la baja se registra como
    // cliente y no encuentra el panel (le pasó a un lead de la captación).
    pathname === "/coaches" ||
    pathname === "/alternativa-harbiz" ||
    pathname.startsWith("/blog-entrenadores") ||
    pathname.startsWith("/coach/") ||
    pathname === "/download" ||
    pathname === "/pago-exitoso" ||
    pathname === "/activar-cuenta" ||
    pathname === "/test"
  );
}

const CLAVE_CERRADA = "campana-bar-cerrada";

export default function CampanaBar() {
  const { pathname } = useLocation();
  const oferta = campanaVigente();

  // Cerrar la barra ahora la calla en TODA la web, no solo en la página donde se
  // pulsó la ×: con una barra global, verla reaparecer al primer clic interno
  // sería justo lo contrario de lo que pidió el visitante. Dura la visita.
  const [cerrada, setCerrada] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(CLAVE_CERRADA) === "1";
    } catch {
      return false;
    }
  });

  const [tienda, setTienda] = useState({ movil: false, url: DOWNLOAD_URL, nombre: null });
  useEffect(() => { setTienda(tiendaDelVisitante()); }, []);

  const barraRef = useRef(null);
  const visible = !cerrada && !rutaExcluida(pathname);

  // Publica el alto real de la barra y lo mantiene al día.
  useEffect(() => {
    const raiz = document.documentElement;
    if (!visible || !barraRef.current) {
      raiz.style.setProperty("--campana-alto", "0px");
      document.body.classList.remove("con-campana");
      return undefined;
    }

    document.body.classList.add("con-campana");
    const nodo = barraRef.current;
    const medir = () => {
      raiz.style.setProperty("--campana-alto", `${Math.round(nodo.getBoundingClientRect().height)}px`);
    };
    medir();

    let observador;
    if (typeof ResizeObserver !== "undefined") {
      observador = new ResizeObserver(medir);
      observador.observe(nodo);
    } else {
      window.addEventListener("resize", medir);
    }

    return () => {
      if (observador) observador.disconnect();
      else window.removeEventListener("resize", medir);
      raiz.style.setProperty("--campana-alto", "0px");
      document.body.classList.remove("con-campana");
    };
  }, [visible, oferta, tienda.movil]);

  const cerrar = useCallback(() => {
    setCerrada(true);
    try { window.sessionStorage.setItem(CLAVE_CERRADA, "1"); } catch { /* modo incógnito */ }
  }, []);

  const irATienda = useCallback(() => {
    if (tienda.nombre === "App Store") trackAppStoreClick("campana_bar");
    else if (tienda.nombre === "Google Play") trackGooglePlayClick("campana_bar");
    else trackCTAClick("campana_bar_download");
  }, [tienda.nombre]);

  if (!visible) return null;

  const cuenta = oferta ? cuentaAtras() : null;
  const destino = tienda.movil ? tienda.url : `${DOWNLOAD_URL}?utm_source=website&utm_medium=barra&utm_campaign=${oferta ? "septiembre" : "evergreen"}`;
  // El botón NO nombra la tienda. Decía "App Store" o "Google Play" según el
  // móvil que lo mirase, y basta con que la detección falle una vez -un
  // navegador raro, un user-agent capado- para que un usuario de Android lea
  // "App Store" y se piense que la app no es para él. El enlace sí sigue yendo
  // a la tienda que toca; lo que se lee es siempre lo mismo.
  const etiquetaCta = "Descargar la app";

  return (
    <div ref={barraRef} className={`campana-bar ${oferta ? "cb-oferta" : "cb-evergreen"}`} role="region" aria-label={oferta ? "Oferta de septiembre" : "Prueba gratuita"}>
      <div className="cb-inner">
        {/* Tres filas centradas: el aviso, el precio y el botón. El precio va
            solo en su fila -tachado + grande- porque es lo único que se lee de
            un vistazo; el resto acompaña. */}
        <span className="cb-titulo">
          {oferta ? (
            <>⚡ Oferta por tiempo limitado{cuenta ? <span className="cb-dias"> · {cuenta}</span> : null}</>
          ) : (
            <>🎁 Accede a todo Wayo · 7 días gratis</>
          )}
        </span>

        <span className="cb-precio">
          {oferta && <s className="cb-antes">{PRECIO_ANTES}</s>}
          <strong className="cb-ahora">{oferta ? PRECIO_AHORA : "gratis"}</strong>
          <span className="cb-periodo">{oferta ? "/año en la app" : "sin límites"}</span>
        </span>

        <a
          className="cb-cta"
          href={destino}
          onClick={irATienda}
          aria-label={tienda.movil ? `Descargar Wayo en ${tienda.nombre}` : "Descargar la app"}
        >
          {etiquetaCta}
        </a>
      </div>

      <button className="cb-cerrar" onClick={cerrar} aria-label="Cerrar el aviso">×</button>
    </div>
  );
}
